import {
  ThemeProvider,
  hexToRgb,
  lighten as muiLighten
} from '@mui/material/styles'

import PropTypes from 'prop-types'

import {useOrganizationCobranding} from './OrganizationCobrandingContext'
import {validHexColor} from '../lib/cobrandingTools'
import {dynamicTheme} from '../styles/muiTheme'

import colors from '../dashboard/styles/abstracts/colors.module.scss'

const normalizeAmount = amount => (amount > 1 ? amount / 100 : amount)

const lighten = (color, amount) => muiLighten(color, normalizeAmount(amount))

const sanitizeHexColor = (color, fallback) => (
  typeof color === 'string' && validHexColor.test(color) ? color : fallback
)

const BrandingThemeProvider = ({children}) => {
  const organizationCobranding = useOrganizationCobranding()

  const primary = sanitizeHexColor(
    organizationCobranding?.enabled ? organizationCobranding.primaryColor : null,
    colors.everplansPrimary
  )

  const proPrimary = sanitizeHexColor(
    organizationCobranding?.enabled ?
      organizationCobranding.primaryColor :
      null,
    colors.everplansProPrimary
  )

  const secondary = sanitizeHexColor(
    organizationCobranding?.enabled ?
      organizationCobranding.secondaryColor :
      null,
    colors.everplansSecondary
  )

  const tertiary = sanitizeHexColor(
    organizationCobranding?.enabled ?
      organizationCobranding.tertiaryColor :
      null,
    colors.everplansTertiary
  )

  const primaryLight = lighten(primary, 0.2)
  const primaryPale = lighten(primary, 0.95)
  const primaryRgb = hexToRgb(primary).replace('rgb(', '').replace(')', '')

  const tertiaryLight = lighten(tertiary, 0.2)
  const tertiaryRgb = hexToRgb(tertiary).replace('rgb(', '').replace(')', '')

  const disabledButtonBackgroundColor = colors.everplansDisabledButtonBackgroundColor
  const disabledButtonColor = colors.everplansDisabledButtonColor

  const outlinedButtonHoverBackgroundColor = primaryPale
  const outlinedButtonHoverColor = lighten(primary, 0.4)

  const sanitizedOrganizationCobranding = organizationCobranding?.enabled ?
    {
      ...organizationCobranding,
      primaryColor: primary,
      secondaryColor: secondary,
      tertiaryColor: tertiary
    } :
    organizationCobranding

  const theme = dynamicTheme(sanitizedOrganizationCobranding)

  return (
    <>
      <style>
        {`
          :root {
            --brand-primary: ${primary};
            --brand-primary-light: ${primaryLight};
            --brand-primary-pale: ${primaryPale};
            --brand-primary-rgb: ${primaryRgb};
            --brand-pro-primary: ${proPrimary};
            --brand-secondary: ${secondary};
            --brand-tertiary: ${tertiary};
            --brand-tertiary-light: ${tertiaryLight};
            --brand-tertiary-rgb: ${tertiaryRgb};
            --brand-disabled-button-background-color: ${disabledButtonBackgroundColor};
            --brand-disabled-button-color: ${disabledButtonColor};
            --brand-outlined-button-hover-background-color: ${outlinedButtonHoverBackgroundColor};
            --brand-outlined-button-hover-color: ${outlinedButtonHoverColor};
          }
        `}
      </style>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </>
  )
}

BrandingThemeProvider.propTypes = {children: PropTypes.node.isRequired}

export default BrandingThemeProvider
